/**
 * Pagination Controls Component for Review Page
 * 
 * Provides client-side pagination controls for large translation datasets
 * without server-side database queries.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal
} from 'lucide-react'
import Button from '../ui/Button'

const PaginationControls = ({ 
  pagination, 
  onPageChange,
  onItemsPerPageChange,
  className = ''
}) => {
  const { t } = useTranslation()

  if (!pagination || pagination.totalPages <= 1) {
    return null
  }

  const {
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    startRow,
    endRow,
    totalRows,
    totalFiltered,
    itemsPerPage
  } = pagination

  // Generate page numbers with smart ellipsis
  const generatePageNumbers = () => {
    const pages = []
    const delta = 2 // Number of pages to show around current page
    const rangeWithDots = []

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      rangeWithDots.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.unshift('...')
    }
    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...')
    }

    rangeWithDots.unshift(1)
    if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    // Remove duplicates and sort
    return [...new Set(rangeWithDots)].sort((a, b) => {
      if (a === '...' || b === '...') return 0
      return a - b
    })
  }

  const pageNumbers = generatePageNumbers()

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 ${className}`}>
      {/* Results summary */}
      <div className="text-sm text-gray-700 dark:text-gray-300">
        {totalFiltered !== undefined && totalFiltered !== totalRows ? (
          <span>
            {t('review.pagination.showingFiltered', { 
              start: startRow, 
              end: endRow, 
              totalFiltered,
              totalRows 
            })}
          </span>
        ) : (
          <span>
            {t('review.pagination.showing', { start: startRow, end: endRow, total: totalRows })}
          </span>
        )}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center space-x-2">
        {/* Items per page selector */}
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            {t('review.pagination.itemsPerPage')}:
          </label>
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(parseInt(e.target.value))}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="hidden sm:block w-px h-6 bg-gray-300 dark:bg-gray-600" />

        {/* Page navigation */}
        <div className="flex items-center space-x-1">
          {/* First page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={!hasPreviousPage}
            title={t('review.pagination.firstPage')}
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>

          {/* Previous page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPreviousPage}
            title={t('review.pagination.previousPage')}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          {/* Page numbers */}
          <div className="flex items-center space-x-1">
            {pageNumbers.map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-2 py-1 text-gray-400">
                    <MoreHorizontal className="w-4 h-4" />
                  </span>
                ) : (
                  <Button
                    variant={currentPage === page ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => onPageChange(page)}
                    className="min-w-[32px]"
                  >
                    {page}
                  </Button>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Next page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNextPage}
            title={t('review.pagination.nextPage')}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>

          {/* Last page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(totalPages)}
            disabled={!hasNextPage}
            title={t('review.pagination.lastPage')}
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PaginationControls